import { FC, PropsWithChildren } from 'react';
import { formatCurrency } from '$/utils/formatCurrency.ts';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'react-daisyui';

type PricingCardProps = {
  title: string;
  description?: string;
  price: number;
  currency?: string;
  pricePeriod?: string;
  features: string[];
  isLoading?: boolean;
  onClick?: () => void;
};

const PricingCard: FC<PricingCardProps> = ({
  title,
  description,
  price,
  currency = 'usd',
  pricePeriod,
  features,
  isLoading = false,
  onClick,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col justify-between max-w-lg p-6 mx-auto text-center bg-base-100 border border-base-300 rounded-lg shadow xl:p-8 w-full">
      <div className="flex flex-col">
        <h3 className="mb-4 text-2xl font-semibold">{title}</h3>
        {description ? <p className="font-light text-text-secondary sm:text-lg">{description}</p> : null}
        <div className="flex items-baseline justify-center my-8">
          <span className="mr-2 text-5xl font-extrabold">
            {formatCurrency(price, currency, {
              calculate: true,
            })}
          </span>
          {pricePeriod ? <span>/{pricePeriod}</span> : null}
        </div>

        <ul role="list" className="mb-8 space-y-4 text-left">
          {features.map((feature, index) => (
            <FeatureRow key={`${feature}_${index}`}>{feature}</FeatureRow>
          ))}
        </ul>
      </div>
      <LoadingButton loading={isLoading} onClick={onClick} color="primary">
        {t('button.continue')}
      </LoadingButton>
    </div>
  );
};

const FeatureRow: FC<PropsWithChildren> = ({ children }) => {
  return (
    <li className="flex items-center space-x-3">
      <svg
        className="flex-shrink-0 w-5 h-5 text-green-500"
        fill="currentColor"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        ></path>
      </svg>
      <span>{children}</span>
    </li>
  );
};

export const SkeletonPricingCard = () => {
  return (
    <div className="flex flex-col justify-between max-w-lg p-6 mx-auto text-center bg-base-100 border border-base-300 rounded-lg shadow xl:p-8 w-full">
      <div className="flex flex-col">
        <Skeleton className="mb-4 h-4 w-full" />
        <div className="flex items-baseline justify-center my-8">
          <Skeleton className="h-14 w-24 mr-2" />
          <Skeleton className="h-4 w-10" />
        </div>

        <ul role="list" className="mb-8 space-y-4 text-left">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </ul>
      </div>
      <Skeleton className="h-12 w-full" />
    </div>
  );
};

export default {
  Card: PricingCard,
  Skeleton: SkeletonPricingCard,
};
